import { AppUpdate, AppUpdateAvailability } from '@capawesome/capacitor-app-update'

import { clientPlatform } from '@/shared/api/platform'
import { APP_STORE_APP_ID } from '@/shared/lib/appStores'

/**
 * Aviso suave de versión nueva de la app (spec `2026-11-02-aviso-actualizacion-app`). La versión
 * disponible la da la propia tienda (Google Play / App Store) vía el plugin: sin backend.
 */

/** Última versión de la que ya se avisó en este dispositivo («una vez por versión»). */
const NOTICED_KEY = 'mp.appUpdate.noticed'

/**
 * Versión nueva de la que aún no se ha avisado, o `null` (web, al día, ya avisada o la consulta
 * falla). Identificador: en iOS el nombre de versión («1.0.9»); en Android, el código de versión
 * (Google Play no da el nombre de la disponible).
 */
export async function pendingUpdate(): Promise<string | null> {
  const platform = clientPlatform()
  if (!platform) return null
  try {
    const info = await AppUpdate.getAppUpdateInfo(
      platform === 'ios' ? { country: 'es' } : undefined,
    )
    if (info.updateAvailability !== AppUpdateAvailability.UPDATE_AVAILABLE) return null
    const version = platform === 'ios' ? info.availableVersionName : info.availableVersionCode
    if (!version || readNoticed() === version) return null
    return version
  } catch {
    // Sin red, instalada fuera de la tienda o tienda sin responder: no se avisa (es un extra).
    return null
  }
}

/** Recuerda que ya se avisó de esta versión. */
export function markNoticed(version: string): void {
  try {
    localStorage.setItem(NOTICED_KEY, version)
  } catch {
    // Memoria del dispositivo no disponible: como mucho se volverá a avisar.
  }
}

function readNoticed(): string | null {
  try {
    return localStorage.getItem(NOTICED_KEY)
  } catch {
    return null
  }
}

/**
 * «Actualizar»: en Android, la actualización inmediata de Google Play dentro de la app (si no se
 * puede —instalada fuera de Play—, la ficha de Play); en iOS, la ficha de la App Store.
 */
export async function startUpdate(): Promise<void> {
  if (clientPlatform() === 'android') {
    try {
      await AppUpdate.performImmediateUpdate()
      return
    } catch {
      await AppUpdate.openAppStore()
      return
    }
  }
  await AppUpdate.openAppStore({ appId: APP_STORE_APP_ID })
}
